import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function HomePage() {
  return (
    <div className="min-h-screen bg-white text-gray-900 font-['Inter',sans-serif]">
      <Navbar />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Stylish Animated Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-orange-50 via-white to-red-50">
          {/* Gradient Orbs */}
          <div className="absolute inset-0">
            <div className="absolute top-0 left-0 w-[600px] h-[600px] bg-gradient-to-br from-orange-400/30 to-red-500/20 rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute top-1/3 right-0 w-[500px] h-[500px] bg-gradient-to-bl from-orange-500/20 to-red-400/30 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
            <div className="absolute bottom-0 left-1/3 w-[400px] h-[400px] bg-gradient-to-tr from-orange-300/25 to-red-300/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
          </div>
          
          {/* Mesh Gradient Overlay */}
          <div className="absolute inset-0 opacity-40" style={{
            backgroundImage: `radial-gradient(circle at 20% 50%, rgba(251, 146, 60, 0.15) 0%, transparent 50%),
                             radial-gradient(circle at 80% 80%, rgba(239, 68, 68, 0.15) 0%, transparent 50%),
                             radial-gradient(circle at 40% 20%, rgba(249, 115, 22, 0.1) 0%, transparent 50%)`
          }}></div>
          
          {/* Animated Grid Pattern */}
          <svg className="absolute inset-0 w-full h-full opacity-[0.03]" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="hero-grid" width="60" height="60" patternUnits="userSpaceOnUse">
                <circle cx="30" cy="30" r="1.5" fill="#ea580c" />
                <line x1="30" y1="0" x2="30" y2="60" stroke="#ea580c" strokeWidth="0.5" />
                <line x1="0" y1="30" x2="60" y2="30" stroke="#ea580c" strokeWidth="0.5" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#hero-grid)" />
          </svg>
          
          {/* Floating Particles */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-orange-500/40 rounded-full animate-ping" style={{ animationDuration: '3s' }}></div>
            <div className="absolute top-2/3 right-1/3 w-2 h-2 bg-red-500/40 rounded-full animate-ping" style={{ animationDuration: '4s', animationDelay: '1s' }}></div>
            <div className="absolute bottom-1/3 left-2/3 w-2 h-2 bg-orange-600/40 rounded-full animate-ping" style={{ animationDuration: '5s', animationDelay: '2s' }}></div>
          </div>
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-6 text-center">
          <div className="mb-6 text-orange-700 text-xs uppercase tracking-[0.2em] font-semibold opacity-80">
            Intelligence, Accelerated
          </div>
          
          <h1 className="text-6xl md:text-7xl lg:text-8xl font-extrabold mb-8 leading-tight">
            <span className="text-gray-900">Intelligent Systems</span>
            <br />
            <span className="bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
              Built for Scale
            </span>
          </h1>

          <p className="text-lg md:text-xl text-gray-700 max-w-3xl mx-auto mb-12 leading-relaxed">
            FluxByte transforms complex data into actionable intelligence through adaptive machine learning, 
            predictive analytics, and enterprise-grade AI automation
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link 
              to="/capabilities"
              className="px-8 py-4 bg-gray-900 text-white rounded-full font-semibold text-base hover:shadow-2xl hover:shadow-gray-900/30 transition-all cursor-pointer whitespace-nowrap"
            >
              Explore AI Solutions →
            </Link>
            <Link 
              to="/contact"
              className="px-8 py-4 border-2 border-orange-600 text-orange-700 rounded-full font-semibold text-base hover:bg-orange-600/10 transition-all cursor-pointer whitespace-nowrap"
            >
              Contact Our Team
            </Link>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <i className="ri-arrow-down-line text-2xl text-orange-600"></i>
        </div>
      </section>

      {/* Quick Overview Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              AI Solutions That Drive Results
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              From machine learning to intelligent automation, we build AI systems that scale with your business
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Link to="/capabilities" className="group bg-white p-8 rounded-xl hover:shadow-xl transition-all cursor-pointer">
              <div className="w-14 h-14 flex items-center justify-center mb-6">
                <i className="ri-brain-line text-4xl bg-gradient-to-br from-orange-600 to-red-600 bg-clip-text text-transparent"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-orange-600 transition-colors">
                AI Capabilities
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed mb-4">
                Explore our full range of machine learning models, automation systems, and intelligent solutions
              </p>
              <span className="text-orange-600 text-sm font-semibold inline-flex items-center gap-1">
                Learn more <i className="ri-arrow-right-line"></i>
              </span>
            </Link>

            <Link to="/use-cases" className="group bg-white p-8 rounded-xl hover:shadow-xl transition-all cursor-pointer">
              <div className="w-14 h-14 flex items-center justify-center mb-6">
                <i className="ri-lightbulb-line text-4xl bg-gradient-to-br from-orange-600 to-red-600 bg-clip-text text-transparent"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-orange-600 transition-colors">
                Real-World Applications
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed mb-4">
                See how businesses use our AI solutions to optimize operations and drive growth
              </p>
              <span className="text-orange-600 text-sm font-semibold inline-flex items-center gap-1">
                View use cases <i className="ri-arrow-right-line"></i>
              </span>
            </Link>

            <Link to="/case-studies" className="group bg-white p-8 rounded-xl hover:shadow-xl transition-all cursor-pointer">
              <div className="w-14 h-14 flex items-center justify-center mb-6">
                <i className="ri-trophy-line text-4xl bg-gradient-to-br from-orange-600 to-red-600 bg-clip-text text-transparent"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-orange-600 transition-colors">
                Success Stories
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed mb-4">
                Read how our clients achieved measurable results with FluxByte AI solutions
              </p>
              <span className="text-orange-600 text-sm font-semibold inline-flex items-center gap-1">
                Read stories <i className="ri-arrow-right-line"></i>
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-orange-600 to-red-600 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Transform Your Business with AI?
          </h2>
          <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
            Let's discuss how FluxByte can build intelligent solutions tailored to your unique challenges
          </p>
          <Link 
            to="/contact"
            className="inline-block px-8 py-4 bg-white text-orange-600 rounded-full font-semibold text-base hover:shadow-2xl transition-all cursor-pointer whitespace-nowrap"
          >
            Get Started Today →
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
